import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardHeader } from '../components/ui/card';
import { CheckCircle2, Zap, TestTube, Cpu, Eye, Settings, FileText, PhoneCall, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { automationTranslations } from '../translations/automation';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Bitrix24Modal from '../components/Bitrix24Modal';
import GalleryLightbox from '../components/GalleryLightbox';
import CertificationBadges from '../components/CertificationBadges';

/**
 * AutomationPage Component
 * 
 * Landing page for Automation & Testing Stations services.
 * Showcases custom automation solutions, testing systems, and PLC integration.
 * 
 * Sections:
 * - Hero: Main value proposition for automation
 * - Capabilities: Core automation capabilities
 * - Systems: Types of automated systems offered
 * - Integration: PLC and technology integration
 * - Benefits: Advantages of automation solutions
 * - Process: Implementation workflow
 * - Final CTA: Contact information
 * - Footer: Company info and locations
 */
const AutomationPage = () => {
  const { language } = useLanguage();
  const t = automationTranslations[language];
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="bg-black min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/automation/hero.jpg"
            alt="Automation Testing Station"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/75 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center md:text-left">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 bg-gray-900/20 border border-[#B91C1C]/50 rounded-full mb-6 backdrop-blur-sm">
              <span className="text-[#B91C1C] text-sm font-semibold">{t.hero.badge}</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {t.hero.title}
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 mb-10 leading-relaxed">
              {t.hero.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                onClick={() => document.getElementById('cta').scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#B91C1C] hover:bg-[#991B1B] text-white px-8 py-6 text-lg font-semibold  shadow-lg shadow-[#B91C1C]/30"
              >
                <FileText className="mr-2" size={20} />
                {t.hero.cta1}
              </Button>
            </div>

            <div className="mt-12 flex flex-wrap gap-8 text-gray-400 text-sm">
              {[t.hero.trust1, t.hero.trust2, t.hero.trust3].map((trust, idx) => (
                <div key={idx} className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-[#B91C1C] rounded-full"></div>
                  <span>{trust}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals - Certification Badges */}
      <section className="py-12 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <CertificationBadges badges={['iso', 'dynalab']} />
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.solutions.title} <span className="text-[#B91C1C]">{t.solutions.titleAccent}</span>
            </h2>
            <div className="w-24 h-1 bg-[#B91C1C] mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {t.solutions.items.map((item, index) => (
              <Card 
                key={index}
                className="bg-gray-800/50 border-gray-700  backdrop-blur-sm"
              >
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <Zap className="text-[#B91C1C]" size={24} />
                    <p className="text-gray-300 text-base leading-relaxed ">
                      {item}
                    </p>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">
            <span className="text-[#B91C1C]">{t.midCta.title}</span>
          </h2>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => document.getElementById('cta').scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white px-10 py-7 text-lg font-semibold  shadow-xl shadow-[#B91C1C]/40"
            >
              {t.midCta.button1}
            </Button>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.gallery.title} <span className="text-[#B91C1C]">{t.gallery.titleAccent}</span>
            </h2>
            <div className="w-24 h-1 bg-[#B91C1C] mx-auto mt-4"></div>
          </div>

          <GalleryLightbox 
            images={[
              {
                src: '/images/automation/gallery1.jpg',
                alt: 'Automation Station 1',
                title: t.gallery.images[0].title,
                caption: t.gallery.images[0].caption
              },
              {
                src: '/images/automation/gallery2.jpg',
                alt: 'Automation Station 2',
                title: t.gallery.images[1].title,
                caption: t.gallery.images[1].caption
              },
              {
                src: '/images/automation/gallery3.jpg',
                alt: 'Automation Station 3',
                title: t.gallery.images[2].title,
                caption: t.gallery.images[2].caption
              }
            ]}
            onQuoteClick={() => setIsModalOpen(true)}
            whatsappMessage={language === 'en' 
              ? 'Hello, I would like to request a quote for automation systems similar to the examples I saw in your gallery.'
              : 'Hola, me gustaría solicitar una cotización para sistemas de automatización similar a los ejemplos que vi en su galería.'
            }
          />
        </div>
      </section>

      {/* Applications */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.applications.title} <span className="text-[#B91C1C]">{t.applications.titleAccent}</span>
            </h2>
            <div className="w-24 h-1 bg-[#B91C1C] mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.applications.items.map((item, index) => (
              <div 
                key={index}
                className="flex items-start space-x-4 bg-gray-800/50 border border-gray-700 rounded-lg p-6  backdrop-blur-sm"
              >
                <TestTube className="text-[#B91C1C] flex-shrink-0 mt-1" size={24} />
                <p className="text-gray-300 text-base leading-relaxed ">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.advantages.title} <span className="text-[#B91C1C]">{t.advantages.titleAccent}</span>
            </h2>
            <div className="w-24 h-1 bg-[#B91C1C] mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {t.advantages.items.map((item, index) => (
              <div 
                key={index}
                className="flex items-start space-x-4 bg-gray-900/50 border border-gray-800 rounded-lg p-6  backdrop-blur-sm"
              >
                <CheckCircle2 className="text-[#B91C1C] flex-shrink-0 mt-1 group-hover:scale-110 transition-transform duration-300" size={24} />
                <p className="text-gray-300 text-base leading-relaxed ">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.process.title} <span className="text-[#B91C1C]">{t.process.titleAccent}</span>
            </h2>
            <div className="w-24 h-1 bg-[#B91C1C] mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.process.steps.map((step, index) => (
              <div 
                key={index}
                className="relative bg-gray-800/50 border border-gray-700 rounded-lg p-6  backdrop-blur-sm"
              >
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-[#B91C1C] rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-[#B91C1C]/50">
                  {index + 1}
                </div>
                <p className="text-gray-300 text-lg leading-relaxed  mt-4">
                  {step}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Final CTA */}
      <section id="cta" className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">
            {t.finalCta.title} <span className="text-[#B91C1C]">{t.finalCta.titleAccent}</span>
          </h2>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => setIsModalOpen(true)}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white px-10 py-7 text-lg font-semibold  shadow-xl shadow-[#B91C1C]/40"
            >
              <FileText className="mr-2" size={22} />
              {t.finalCta.button1}
            </Button>
            <Button 
              size="lg"
              onClick={() => window.open('https://wa.me/526181719959?text=Hola,%20me%20interesa%20conocer%20sus%20soluciones%20de%20automatización', '_blank')}
              className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-10 py-7 text-lg font-semibold  shadow-xl shadow-[#25D366]/40"
            >
              <MessageCircle className="mr-2" size={22} />
              WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* Footer with Contact Section */}
      <Footer />
      
      {/* Bitrix24 Modal */}
      <Bitrix24Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default AutomationPage;
